import type { IntegerRect } from "./selectionTypes";
import {
  MAX_SELECTION_REGION_BUFFER_BYTES,
  MAX_SELECTION_TEMP_SURFACE_BYTES,
  selectionFitsMemoryLimits,
} from "./selectionMemory";

/**
 * An owned, read-only pixel payload. The backing typed array is private and is
 * never returned. Rendering creates an ImageData view over the owned array and
 * synchronously copies it into an isolated region canvas, so no third
 * region-sized RGBA array exists at the live peak.
 */
export class ImmutableRasterPixelBuffer {
  private constructor(
    public readonly width: number,
    public readonly height: number,
    private readonly pixels: Uint8ClampedArray<ArrayBuffer>,
    public readonly hasVisiblePixels: boolean,
  ) {
    Object.freeze(this);
  }

  public static capture(
    context: CanvasRenderingContext2D,
    rect: Readonly<IntegerRect>,
  ): ImmutableRasterPixelBuffer {
    if (!selectionFitsMemoryLimits(rect)) {
      throw new Error("That selection is too large for this tablet. Try a smaller area.");
    }
    const imageData = context.getImageData(rect.x, rect.y, rect.width, rect.height);
    return new ImmutableRasterPixelBuffer(
      rect.width,
      rect.height,
      imageData.data,
      containsVisiblePixels(imageData.data),
    );
  }

  public static fromPixels(
    width: number,
    height: number,
    pixels: Uint8ClampedArray,
  ): ImmutableRasterPixelBuffer {
    const rect = { x: 0, y: 0, width, height };
    if (!selectionFitsMemoryLimits(rect) || pixels.byteLength !== width * height * 4) {
      throw new Error("Raster selection pixels are invalid or too large.");
    }
    const copy = new Uint8ClampedArray(pixels);
    return new ImmutableRasterPixelBuffer(width, height, copy, containsVisiblePixels(copy));
  }

  public get byteLength(): number {
    return this.pixels.byteLength;
  }

  public createRenderingCanvas(): HTMLCanvasElement {
    if (
      this.pixels.byteLength > MAX_SELECTION_REGION_BUFFER_BYTES ||
      this.width * this.height * 4 > MAX_SELECTION_TEMP_SURFACE_BYTES
    ) {
      throw new Error("Raster selection pixels exceed the transform memory limit.");
    }
    if (typeof ImageData !== "function") {
      throw new Error("This browser cannot safely prepare selected pixels.");
    }
    const canvas = document.createElement("canvas");
    canvas.width = this.width;
    canvas.height = this.height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D is unavailable for the selected pixels.");

    // The evergreen-browser ImageData(data, width, height) constructor retains
    // the supplied Uint8ClampedArray as its data view. putImageData is
    // synchronous, and neither the ImageData nor its private backing array is
    // exposed by this API. The returned canvas owns the second and only other
    // full region-sized allocation.
    const imageData = new ImageData(this.pixels, this.width, this.height);
    context.putImageData(imageData, 0, 0);
    return canvas;
  }

  public copyPixelsForTest(): Uint8ClampedArray {
    return new Uint8ClampedArray(this.pixels);
  }
}

function containsVisiblePixels(pixels: Uint8ClampedArray): boolean {
  for (let index = 3; index < pixels.length; index += 4) {
    if (pixels[index] !== 0) return true;
  }
  return false;
}

export function createRasterPixelCanvas(buffer: ImmutableRasterPixelBuffer): HTMLCanvasElement {
  if (buffer.byteLength > MAX_SELECTION_TEMP_SURFACE_BYTES) {
    throw new Error("The selection needs too much temporary drawing memory.");
  }
  return buffer.createRenderingCanvas();
}
